import { Spinner2 } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CARD_CLASS =
  'bg-white border border-[#e5e7eb] rounded-xl p-3 sm:p-6 flex flex-col';

const PERIODS = ['30d', '90d', '1y', 'all'];

const fmt = (n) =>
  n != null ? n.toLocaleString() : '—';

const fmtRate = (rate) =>
  rate != null ? `${rate.toFixed(2)}%` : '—';

const providerRate = (s, f) => {
  const total = s + f;
  if (total === 0) return '—';
  return `${((s / total) * 100).toFixed(2)}%`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const RenewalsCard = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRenewals(period);
  }, [period]);

  async function fetchRenewals(selectedPeriod) {
    setLoading(true);
    const res = await Api.get(
      `/admin/subscriptionPlans/overviewRenewals?period=${selectedPeriod}`
    );
    if (res?.data) {
      setData(res.data);
    }
    setLoading(false);
  }

  const isAllTime = period === 'all';
  const hasData = data && (data.total > 0 || (data.byProvider && data.byProvider.length > 0));
  const computedRate =
    data?.successRate != null
      ? data.successRate
      : data?.total > 0
        ? (data.successCount / data.total) * 100
        : null;

  return (
    <div className={CARD_CLASS}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[15px] font-semibold text-[#353E4A]">
            {t('subscriptions.admin.overview.renewals.title')}
          </p>
          {!isAllTime && data?.periodStart && data?.periodEnd && (
            <p className="text-[11px] text-[#97A3B5] mt-0.5">
              {new Date(data.periodStart).toLocaleDateString()} –{' '}
              {new Date(data.periodEnd).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Period filter */}
      <div className="flex flex-wrap items-center rounded-lg border border-[#D7DEE8] bg-[#F1F5F9] p-1 mb-4 self-start gap-0.5 shadow-[inset_0_1px_1px_rgba(15,23,42,0.06)]">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            aria-pressed={period === p}
            className={`px-2 py-1 rounded-md text-[10px] sm:text-[11px] font-semibold transition-all duration-150 whitespace-nowrap ${
              period === p
                ? 'bg-[#334155] text-white shadow-[0_2px_6px_rgba(15,23,42,0.28)]'
                : 'text-[#64748B] hover:text-[#334155] hover:bg-white/70'
            }`}
          >
            {t(`subscriptions.admin.overview.renewals.periods.${p}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-10">
          <Spinner2 />
        </div>
      ) : !hasData ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
          <div className="w-12 h-12 rounded-xl bg-[#F0F2F4] flex items-center justify-center mb-4">
            <svg
              className="w-5 h-5 text-[#B5BDCA]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <p className="text-[13px] text-[#97A3B5]">{t('subscriptions.admin.overview.renewals.empty')}</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 gap-4">
          {/* KPI tiles */}
          <div className="grid grid-cols-3 gap-2">
            {/* Success */}
            <div className="rounded-lg bg-[#F0FDF4] border border-[#BBF7D0] px-1 py-1 sm:px-2 sm:py-2">
              <div className="flex items-start gap-0.5 mb-1">
                <span className="text-[8px] sm:text-[9px] shrink-0 leading-tight">✅</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-[#16A34A] uppercase tracking-wide leading-tight break-words">
                  {t('subscriptions.admin.overview.renewals.successes')}
                </span>
              </div>
              <p className="text-[14px] sm:text-[18px] font-bold text-[#15803D] leading-none">
                {fmt(data?.successCount)}
              </p>
            </div>

            {/* Failure */}
            <div className="rounded-lg bg-[#FFF1F2] border border-[#FECDD3] px-1 py-1 sm:px-2 sm:py-2">
              <div className="flex items-start gap-0.5 mb-1">
                <span className="text-[8px] sm:text-[9px] shrink-0 leading-tight">❌</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-[#E11D48] uppercase tracking-wide leading-tight break-words">
                  {t('subscriptions.admin.overview.renewals.failures')}
                </span>
              </div>
              <p className="text-[14px] sm:text-[18px] font-bold text-[#BE123C] leading-none">
                {fmt(data?.failureCount)}
              </p>
            </div>

           {/* Success Rate */}
            <div className="rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] px-1 py-1 sm:px-2 sm:py-2">
              <div className="flex items-start gap-0.5 mb-1">
                <span className="text-[8px] sm:text-[9px] shrink-0 leading-tight">📊</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-[#1D4ED8] uppercase tracking-wide leading-tight break-words">
                  {t('subscriptions.admin.overview.renewals.successRate')}
                </span>
              </div>
              <p className="text-[14px] sm:text-[18px] font-bold text-[#1E40AF] leading-none">
                {fmtRate(computedRate)}
              </p>
            </div>
          </div>

          {/* Provider breakdown table */}
          {data?.byProvider && data.byProvider.length > 0 && (
            <div className="rounded-lg border border-[#e5e7eb] overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[260px]">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#e5e7eb]">
                    <th className="px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-[#7989A0] uppercase tracking-wide">
                      {t('subscriptions.admin.overview.renewals.provider')}
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-[#7989A0] uppercase tracking-wide">
                      {t('subscriptions.admin.overview.renewals.successes')}
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-[#7989A0] uppercase tracking-wide">
                      {t('subscriptions.admin.overview.renewals.failures')}
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold text-[#7989A0] uppercase tracking-wide">
                      {t('subscriptions.admin.overview.renewals.successRate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byProvider.map((row, i) => (
                    <tr
                      key={row.provider}
                      className={`border-b border-[#f1f5f9] last:border-0 ${
                        i % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'
                      }`}
                    >
                      <td className="px-2 sm:px-3 py-2 text-[11px] sm:text-[13px] font-medium text-[#353E4A] whitespace-nowrap">
                        {capitalize(row.provider)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-[11px] sm:text-[13px] text-[#16A34A] font-medium text-center whitespace-nowrap">
                        {fmt(row.successCount)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-[11px] sm:text-[13px] text-[#E11D48] font-medium text-center whitespace-nowrap">
                        {fmt(row.failureCount)}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-[11px] sm:text-[13px] text-[#1D4ED8] font-medium text-center whitespace-nowrap">
                        {providerRate(row.successCount, row.failureCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RenewalsCard;
